import { motion, useReducedMotion } from 'motion/react';
import { NOTIFICATION_ENTRANCE_TRANSITION } from '@/pages/onboarding/mockup/notification-motion';

// Mirrors backend's NOTIFICATION_PREVIEW_TITLE_MAX_LENGTH /
// NOTIFICATION_PREVIEW_BODY_MAX_LENGTH (backend/src/controllers/dtos/
// create-tenant-creation-request.dto.ts) — step 5's form (T22) should reuse
// these same constants for its own validation, so this preview always
// truncates at the exact limit the backend enforces.
export const NOTIFICATION_PREVIEW_TITLE_MAX_LENGTH = 50;
export const NOTIFICATION_PREVIEW_BODY_MAX_LENGTH = 150;

// Sample copy shown until the merchant types their own — an example of what
// *they* would write as the notification's title/body, not the app's own
// name (a notification's title is free text the sender chooses, same as any
// push notification, so the placeholder should read like one).
export const SAMPLE_NOTIFICATION_TITLE = 'Sua oferta especial chegou! 🎉';
export const SAMPLE_NOTIFICATION_BODY =
  'Aproveite descontos exclusivos por tempo limitado.';

function truncate(value: string, maxLength: number): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}

export interface NotificationBannerProps {
  appIconUrl: string | null;
  title: string;
  body: string;
  // Whether the banner should be slid into view. Rendered unconditionally
  // by the caller (not mounted/unmounted on a timer) and driven by this
  // prop instead: React 18 StrictMode's dev-only double-invoke of mount
  // effects was found to swallow Motion's *mount-triggered* initial→animate
  // transition (the entrance never played, confirmed via onAnimationStart
  // never firing, even though the DOM committed the correct initial/animate
  // props). An *update-triggered* animation — an already-mounted element
  // whose `animate` target changes after a state flip, the same pattern
  // NotificationPreviewPage's own phone-slide motion.div already relies on
  // — isn't affected, so entrance is modeled that way here too instead of
  // via conditional mounting.
  visible: boolean;
}

// `y` here is a percentage of the banner's OWN height, applied on top of
// its resting `top-14` (56px) — it doesn't grow to compensate for that
// offset by itself. -150% was tuned back when the banner rested at `top-3`
// (an offset small enough to mostly ignore): translated up by 1.5x its own
// ~64px height, its bottom edge landed safely above y=0. Moving the resting
// position down to `top-14` without widening this made the same 1.5x
// translation fall short — the banner's bottom edge now lands ~24px *into*
// the visible screen while still "hidden", not above it. -300% has enough
// margin to clear `top-14` across the banner's realistic height range
// (title + 1-2 lines of body), so its bottom edge stays off-screen at rest
// exactly like a real notification sliding out from under the Dynamic
// Island, rather than starting half-visible.
const ENTRANCE_OFFSCREEN_Y = '-300%';

export function NotificationBanner({
  appIconUrl,
  title,
  body,
  visible,
}: NotificationBannerProps) {
  const shouldReduceMotion = useReducedMotion();
  const displayTitle = title
    ? truncate(title, NOTIFICATION_PREVIEW_TITLE_MAX_LENGTH)
    : SAMPLE_NOTIFICATION_TITLE;
  const displayBody = body
    ? truncate(body, NOTIFICATION_PREVIEW_BODY_MAX_LENGTH)
    : SAMPLE_NOTIFICATION_BODY;

  return (
    <motion.div
      initial={false}
      animate={{
        y: visible ? '0%' : ENTRANCE_OFFSCREEN_Y,
      }}
      transition={
        shouldReduceMotion ? { duration: 0 } : NOTIFICATION_ENTRANCE_TRANSITION
      }
      // `top-14` (not `top-3`) — real iOS notification banners sit *below*
      // the Dynamic Island/status bar, never under or overlapping it. The
      // Island itself is drawn by react-device-mockup's own frame chrome,
      // outside this component's (and IPhoneMockup's) DOM entirely, so
      // nothing here can rely on z-index to stay clear of it — only
      // starting low enough does. `pt-14` on StoreContentSkeleton
      // (app-open-scene.tsx) clears the same chrome for the same reason.
      className="absolute inset-x-3 top-14 z-10 flex items-start gap-2.5 rounded-2xl bg-white/90 p-3 shadow-lg backdrop-blur"
    >
      <div className="h-8 w-8 shrink-0 overflow-hidden rounded-[22%] bg-muted">
        {appIconUrl ? (
          <img src={appIconUrl} alt="" className="h-full w-full object-cover" />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[13px] font-semibold text-black">
            {displayTitle}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground">
            agora
          </span>
        </div>
        <p className="line-clamp-2 text-[12px] text-black/80">{displayBody}</p>
      </div>
    </motion.div>
  );
}
